import { useState, useEffect } from "react";
import { X } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/router";

export type HoverNotificationType =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "message"
  | "order"
  | "like";

export interface HoverNotification {
  id: string;
  notificationId?: string;
  type: HoverNotificationType;
  stockSymbol: string;
  stockName: string;
  newsTitle: string;
  moreInfoLink: string;
  time?: string;
  avatar?: string;
  category?: string;
}

interface HoverStackProps {
  notifications: HoverNotification[];
  variant: "compact";
  onRemove?: (id: string) => void;
  onClearAll?: () => void;
}

export const HoverNotificationStack = ({
  notifications,
  variant,
  onRemove,
  onClearAll,
}: HoverStackProps) => {
  const [localNotifications, setLocalNotifications] =
    useState<HoverNotification[]>(notifications);
  const [isHovered, setIsHovered] = useState(false);
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());
  const router = useRouter();

  useEffect(() => {
    const propIds = new Set(notifications.map((n) => n.id));

    const syncTimer = setTimeout(() => {
      setLocalNotifications((prev) => {
        const filtered = prev.filter((ln) => propIds.has(ln.id));
        if (filtered.length !== prev.length) return filtered;
        return prev;
      });
    }, 0);

    const toAdd = notifications.filter(
      (n) => !localNotifications.some((ln) => ln.id === n.id),
    );

    let additionTimer: NodeJS.Timeout | null = null;
    if (toAdd.length > 0) {
      additionTimer = setTimeout(() => {
        setLocalNotifications((prev) => {
          const next = notifications.find(
            (n) => !prev.some((p) => p.id === n.id),
          );
          if (next) return [...prev, next];
          return prev;
        });
      }, 600);
    }

    return () => {
      clearTimeout(syncTimer);
      if (additionTimer) clearTimeout(additionTimer);
    };
  }, [notifications, localNotifications]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSeenIds((prev) => {
        const newSeen = new Set(prev);
        localNotifications.forEach((n) => newSeen.add(n.id));
        return newSeen;
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, [localNotifications]);

  const handleRemove = (id: string) => {
    setLocalNotifications((prev) => prev.filter((n) => n.id !== id));
    onRemove?.(id);
  };

  const handleClearAll = () => {
    setLocalNotifications([]);
    onClearAll?.();
  };

  if (localNotifications.length === 0) return null;

  const renderCompact = () => {
    return (
      <div
        className="relative pointer-events-auto flex flex-col items-end"
        onMouseLeave={() => setIsHovered(false)}
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
          .scrollbar-hide::-webkit-scrollbar { display: none; }
          .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        `,
          }}
        />
        <div
          className={`relative w-80 transition-all duration-500 ease-in-out ${
            isHovered
              ? "overflow-y-auto scrollbar-hide flex flex-col-reverse gap-2"
              : "overflow-visible"
          }`}
          style={{
            maxHeight: isHovered ? "calc(100vh - 150px)" : "80px",
            height: isHovered
              ? `${localNotifications.length * 80 + 10}px`
              : "80px",
            marginBottom:
              localNotifications.length > 1 && isHovered ? "40px" : "0px",
          }}
        >
          {localNotifications.map((notification, index) => {
            const invIndex = localNotifications.length - 1 - index;
            const isNew = !seenIds.has(notification.id);
            const animationClass = isNew ? "animate-notification-entrance" : "";
            const startClass = isNew ? "notification-entrance-start" : "";

            const isTopCard = index === localNotifications.length - 1;

            return (
              <div
                key={notification.id}
                onMouseEnter={isTopCard ? () => setIsHovered(true) : undefined}
                className={`absolute bottom-0 right-0 w-80 transition-all duration-500 ease-[cubic-bezier(0.19,1,0.22,1)] ${startClass} ${animationClass}`}
                style={{
                  zIndex: index + 1,
                  animationDelay: isNew ? "400ms" : "0ms",
                }}
              >
                <div
                  className={`relative p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 shadow-sm backdrop-blur-lg group/card transition-all duration-300`}
                  style={{
                    transform: isHovered
                      ? `translateY(${invIndex * -78}px)`
                      : `translateY(${invIndex * -8}px) scale(${1 - invIndex * 0.03})`,
                    opacity: isHovered ? 1 : 1 - invIndex * 0.1,
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(notification.id);
                    }}
                    className="absolute top-2 right-2 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 transition-colors cursor-pointer"
                    aria-label="Close"
                  >
                    <X size={14} />
                  </button>
                  <div className="flex items-start gap-3.5 pr-4">
                    <div
                      className={`p-1 rounded-lg flex-shrink-0 flex items-center justify-center w-11 h-11 overflow-hidden shadow-sm`}
                    >
                      <Image
                        src="/images/favicon.png"
                        alt="Abillion Analytics"
                        width={44}
                        height={44}
                        className="object-contain"
                        style={{ maxHeight: "100%", maxWidth: "100%" }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-medium text-gray-700 dark:text-gray-200 leading-normal line-clamp-2 mb-1">
                        {notification.newsTitle}
                      </p>
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => {
                            const targetId =
                              notification.notificationId || notification.id;
                            sessionStorage.setItem(
                              "highlight_notification_id",
                              targetId,
                            );
                            if (notification.category) {
                              sessionStorage.setItem(
                                "notification_category",
                                notification.category,
                              );
                            }
                            handleClearAll();
                            router.push(notification.moreInfoLink);
                          }}
                          className="text-[11px] font-medium text-blue-500 dark:text-blue-400 hover:underline cursor-pointer border-none bg-transparent p-0 flex items-center gap-1 group"
                        >
                          Get more info
                        </button>
                        {notification.time && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {notification.time}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {localNotifications.length > 1 && (
          <div
            className={`absolute bottom-[-10px] right-0 transition-all duration-500 flex justify-center w-full z-[1001] ${
              isHovered
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2 pointer-events-none"
            }`}
          >
            <button
              onClick={handleClearAll}
              className="px-6 py-2 rounded-full bg-white/90 dark:bg-gray-800/90 border border-gray-100 dark:border-gray-700 text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-all duration-300 cursor-pointer backdrop-blur-md shadow-sm"
            >
              Clear all
            </button>
          </div>
        )}
      </div>
    );
  };

  switch (variant) {
    case "compact":
      return renderCompact();
    default:
      return renderCompact();
  }
};
